import {
  getWorkflowGuide,
  guideUsesCompanySelect,
  guideUsesMultiCompanySelect,
  guideUsesProfileCompanySelect,
  PE_REPORT_MAX_COMPANIES,
  PE_REPORT_MIN_COMPANIES,
} from "./workflowGuides";
import ReportBlockBuilder from "./ReportBlockBuilder";
import { filterCatalogForBase } from "./reportProfileDefaults";
import { ASSIGN_ALL_VALUE } from "./ProfileAssignmentForm";

export default function WorkflowGuideCard({
  guideId,
  values,
  onChange,
  onSubmit,
  onSecondarySubmit,
  onCancel,
  busy = false,
  error = "",
  companyOptions = [],
  companiesLoading = false,
  blockCatalog = [],
  blocksCatalogLoading = false,
}) {
  const guide = getWorkflowGuide(guideId);
  if (!guide) return null;

  const needsCompanyPicker = guideUsesCompanySelect(guideId);
  const needsMultiCompanyPicker = guideUsesMultiCompanySelect(guideId);
  const selectedCompanies = Array.isArray(values.companies) ? values.companies : [];
  const emptyCompaniesLabel =
    guideId === "lite" ? "No companies with a completed LITE assessment" : "No report-ready companies";
  const emptyCompaniesHelp =
    guideId === "lite"
      ? "No LITE report-ready companies yet. Complete a LITE assessment first, or type the company name in the command bar above."
      : "No report-ready companies yet. Complete enough assessments in Insights first, or type the company name in the command bar above.";

  const canSubmit = (() => {
    if (busy) return false;
    if (needsCompanyPicker) {
      return !companiesLoading && !!values.company?.trim() && companyOptions.length > 0;
    }
    if (needsMultiCompanyPicker) {
      return (
        !companiesLoading &&
        companyOptions.length > 0 &&
        selectedCompanies.length >= PE_REPORT_MIN_COMPANIES &&
        selectedCompanies.length <= PE_REPORT_MAX_COMPANIES
      );
    }
    if (guideId === "profile") {
      const blocks = Array.isArray(values.blocks) ? values.blocks : [];
      return (
        !!String(values.name ?? "").trim() &&
        !!String(values.reportBase ?? "").trim() &&
        blocks.some((b) => b.enabled !== false)
      );
    }
    return true;
  })();

  const profileFilteredCatalog =
    guideId === "profile"
      ? filterCatalogForBase(blockCatalog, values.reportBase || "company")
      : [];

  const companyPickerBlocked =
    (needsCompanyPicker || needsMultiCompanyPicker) &&
    !companiesLoading &&
    companyOptions.length === 0;

  const toggleCompany = (name) => {
    const current = new Set(selectedCompanies);
    if (current.has(name)) {
      current.delete(name);
    } else if (current.size < PE_REPORT_MAX_COMPANIES) {
      current.add(name);
    }
    onChange("companies", [...current]);
  };

  return (
    <div className="rounded-2xl border-2 border-[#004f73]/25 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-gradient-to-r from-[#004f73]/8 to-transparent border-b border-[#004f73]/10">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#004f73]">
              Workflow
            </p>
            <h3 className="text-base font-semibold text-slate-900">{guide.title}</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{guide.intro}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="shrink-0 text-sm text-slate-500 hover:text-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {guide.fields.map((field) => {
          const id = `workflow-guide-${guideId}-${field.key}`;
          return (
            <div key={field.key}>
              <label htmlFor={id} className="block text-sm font-medium text-slate-800 mb-1">
                {field.label}
                {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  id={id}
                  rows={3}
                  value={values[field.key] ?? ""}
                  disabled={busy}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full text-sm rounded-xl border border-slate-200 px-3.5 py-2.5 focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 disabled:bg-slate-50"
                />
              ) : field.type === "companiesMulti" ? (
                <div
                  id={id}
                  className="rounded-xl border border-slate-200 p-3 max-h-52 overflow-y-auto space-y-2 bg-slate-50/50"
                >
                  {companiesLoading ? (
                    <p className="text-sm text-slate-500">Loading companies…</p>
                  ) : companyOptions.length ? (
                    companyOptions.map((name) => {
                      const checked = selectedCompanies.includes(name);
                      const atCap =
                        !checked && selectedCompanies.length >= PE_REPORT_MAX_COMPANIES;
                      return (
                        <label
                          key={name}
                          className={`flex items-center gap-2.5 text-sm rounded-lg px-2 py-1.5 cursor-pointer ${
                            atCap ? "opacity-50 cursor-not-allowed" : "hover:bg-white"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy || atCap}
                            onChange={() => toggleCompany(name)}
                            className="rounded border-slate-300 text-[#004f73] focus:ring-[#004f73]/30"
                          />
                          <span className="text-slate-800">{name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="text-sm text-slate-500">{emptyCompaniesLabel}</p>
                  )}
                </div>
              ) : field.type === "companySelect" ? (
                <select
                  id={id}
                  value={values[field.key] ?? ""}
                  disabled={busy || companiesLoading}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full text-sm rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 disabled:bg-slate-50"
                >
                  <option value="">
                    {companiesLoading
                      ? "Loading companies…"
                      : companyOptions.length
                        ? field.placeholder || "Select a company"
                        : emptyCompaniesLabel}
                  </option>
                  {companyOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : field.type === "profileCompanySelect" ? (
                <select
                  id={id}
                  value={values[field.key] ?? ASSIGN_ALL_VALUE}
                  disabled={busy || companiesLoading}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full text-sm rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 disabled:bg-slate-50"
                >
                  <option value={ASSIGN_ALL_VALUE}>
                    {companiesLoading ? "Loading companies…" : "All companies"}
                  </option>
                  {companyOptions.length > 0 ? (
                    <option disabled>──────────────</option>
                  ) : null}
                  {companyOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : field.type === "profileBlocks" ? (
                <div
                  id={id}
                  className="rounded-xl border border-slate-200 bg-white p-3 min-h-[280px] max-h-[min(52vh,520px)] overflow-y-auto"
                >
                  {blocksCatalogLoading ? (
                    <p className="text-sm text-slate-500 py-6 text-center">Loading blocks…</p>
                  ) : (
                    <ReportBlockBuilder
                      catalog={profileFilteredCatalog}
                      selectedBlocks={Array.isArray(values.blocks) ? values.blocks : []}
                      onChange={(next) => onChange(field.key, next)}
                      touchReorder
                    />
                  )}
                </div>
              ) : field.type === "select" ? (
                <select
                  id={id}
                  value={values[field.key] ?? ""}
                  disabled={busy}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full text-sm rounded-xl border border-slate-200 px-3.5 py-2.5 bg-white focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 disabled:bg-slate-50"
                >
                  {(field.options || []).map((opt) => (
                    <option key={opt.value || "empty"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={id}
                  type="text"
                  value={values[field.key] ?? ""}
                  disabled={busy}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full text-sm rounded-xl border border-slate-200 px-3.5 py-2.5 focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 disabled:bg-slate-50"
                />
              )}
              {field.type === "companiesMulti" ? (
                <p className="text-xs text-slate-500 mt-1">
                  {selectedCompanies.length}/{PE_REPORT_MAX_COMPANIES} selected
                  {selectedCompanies.length < PE_REPORT_MIN_COMPANIES
                    ? ` · pick at least ${PE_REPORT_MIN_COMPANIES}`
                    : ""}
                </p>
              ) : field.hint ? (
                <p className="text-xs text-slate-500 mt-1">{field.hint}</p>
              ) : null}
            </div>
          );
        })}

        {companyPickerBlocked ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {emptyCompaniesHelp}
          </p>
        ) : null}

        {error ? (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          {guideId === "pe" && guide.secondarySubmitLabel ? (
            <>
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className="flex-1 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003d5c] disabled:opacity-50 rounded-xl px-4 py-2.5 transition-colors"
              >
                {busy ? "Working…" : guide.submitLabel}
              </button>
              <button
                type="button"
                onClick={onSecondarySubmit}
                disabled={!canSubmit || busy}
                className="flex-1 text-sm font-semibold text-[#004f73] border border-[#004f73]/30 bg-white hover:bg-[#004f73]/5 disabled:opacity-50 rounded-xl px-4 py-2.5 transition-colors"
              >
                {guide.secondarySubmitLabel}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="flex-1 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003d5c] disabled:opacity-50 rounded-xl px-4 py-2.5 transition-colors"
            >
              {busy ? "Working…" : guide.submitLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm font-medium text-slate-700 border border-slate-200 rounded-xl px-4 py-2.5 hover:bg-slate-50 disabled:opacity-50 sm:w-auto w-full"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
